/* ==========================================================================
   HIZEN x Cannafair 2026 — Gewinnspiel ("Dreh & Gewinn")
   Custom Element <cannafair-game>, Theme-Konvention: defer + Guard-Pattern.
   Alles in einer IIFE gekapselt — theme.liquid definiert global u.a.
   debounce, KEYCODES, rbi, rbiSetSize, imageReveal, KROWN, KrownAssetLoader
   (theme-report Risiko #14): KEINE globalen const/let auf Top-Level.
   ========================================================================== */
(function () {
  'use strict';

  /* --- 1. Ziehung — reine Funktionen, isoliert testbar ------------------
     Browser: window.__cannafairDraw.drawPrize(prizes)
     Node:    require('<pfad>/cannafair-game.js').drawPrize(prizes)      */

  var DEFAULT_PRIZES = [
    { tier: 1, label: '10 %', prizeLabel: '10 % Rabattcode', weight: 70 },
    { tier: 2, label: '20 %', prizeLabel: '20 % Rabattcode', weight: 25 },
    { tier: 3, label: 'GRINDER', prizeLabel: 'Gratis-Grinder', weight: 5 }
  ];

  /** Kryptografisch starke Gleichverteilung in [0, 1). */
  function randomFloat() {
    var root = typeof globalThis !== 'undefined' ? globalThis : window;
    var c = root.crypto || root.msCrypto;
    if (c && typeof c.getRandomValues === 'function') {
      var buf = new Uint32Array(1);
      c.getRandomValues(buf);
      return buf[0] / 4294967296;
    }
    return Math.random();
  }

  /**
   * Gewichtete Ziehung. Die Gewichte muessen sich nicht auf 100 summieren —
   * es wird immer gegen die Gesamtsumme normalisiert.
   * @param {Array<{tier:number,weight:number}>} prizes
   * @param {Function} [rnd] optionale RNG-Quelle (fuer Unit-Tests)
   * @returns {Object} das gezogene Prize-Objekt aus der uebergebenen Liste
   */
  function drawPrize(prizes, rnd) {
    var list = prizes && prizes.length ? prizes : DEFAULT_PRIZES;
    var total = 0;
    var i;
    for (i = 0; i < list.length; i++) {
      total += Math.max(0, Number(list[i].weight) || 0);
    }
    if (!(total > 0)) return list[list.length - 1];

    var r = (typeof rnd === 'function' ? rnd() : randomFloat()) * total;
    var acc = 0;
    for (i = 0; i < list.length; i++) {
      acc += Math.max(0, Number(list[i].weight) || 0);
      if (r < acc) return list[i];
    }
    return list[list.length - 1];
  }

  /** Es zaehlt das bessere der beiden Ergebnisse (Bonus-Dreh, Legal § 5 Abs. 4). */
  function betterPrize(a, b) {
    if (!a) return b || null;
    if (!b) return a;
    return Number(b.tier) > Number(a.tier) ? b : a;
  }

  var drawApi = {
    DEFAULT_PRIZES: DEFAULT_PRIZES,
    randomFloat: randomFloat,
    drawPrize: drawPrize,
    betterPrize: betterPrize
  };

  if (typeof window !== 'undefined') window.__cannafairDraw = drawApi;
  if (typeof module !== 'undefined' && module.exports) module.exports = drawApi;

  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('cannafair-game')) return;

  /* --- 2. Konstanten & Speicher-Helfer -------------------------------- */

  var SEGMENT_TIERS = [1, 2, 1, 3, 1, 2]; // muss zur Segment-Reihenfolge im Liquid passen
  var SEGMENT_DEG = 360 / SEGMENT_TIERS.length;
  var SESSION_KEY = 'cannafair:session:v1';
  var PLAYED_KEY = 'cannafair:played:v1';
  var AGE_KEY = 'cannafair:age:v1';
  var THEME_AGE_KEY = 'modal-' + location.hostname + '-modal-popup-age-verification';
  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var FLOW = ['age', 'intro', 'spin', 'bonus', 'form', 'done'];
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

  function store(kind) {
    try {
      return kind === 'session' ? window.sessionStorage : window.localStorage;
    } catch (e) {
      return null;
    }
  }

  function readJSON(kind, key) {
    var s = store(kind);
    if (!s) return null;
    try {
      var raw = s.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeJSON(kind, key, value) {
    var s = store(kind);
    if (!s) return;
    try {
      s.setItem(key, JSON.stringify(value));
    } catch (e) {
      /* Private Mode / Quota — der Ablauf funktioniert auch ohne Persistenz. */
    }
  }

  function removeKey(kind, key) {
    var s = store(kind);
    if (!s) return;
    try {
      s.removeItem(key);
    } catch (e) {}
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function focusSoftly(el) {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  /* --- 3. Custom Element ---------------------------------------------- */

  class CannafairGame extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;

      this.o = this.parseOptions();
      this.strings = this.o.strings || {};
      this.prizes = Array.isArray(this.o.prizes) && this.o.prizes.length ? this.o.prizes : DEFAULT_PRIZES;

      this.injectRobots();
      this.cacheDom();
      this.bind();
      this.state = this.restore();

      // Bewusst KEIN Fokus beim ersten Rendern: das theme-eigene Age-Modal
      // (popup-age-verification, disableScroll: true) liegt beim Laden ueber
      // der Seite — theme-report Risiko #3 / #7 / #15.
      this.render(false);
    }

    /* ---------------------------------------------------------- Setup --- */

    parseOptions() {
      try {
        return JSON.parse(this.dataset.options || '{}');
      } catch (e) {
        return {};
      }
    }

    injectRobots() {
      if (!this.o.noindex) return;
      if (document.querySelector('meta[name="robots"][data-cannafair]')) return;
      var m = document.createElement('meta');
      m.name = 'robots';
      m.content = 'noindex,nofollow';
      m.setAttribute('data-cannafair', '');
      document.head.appendChild(m); // muss in den <head>, nicht in <main>
    }

    cacheDom() {
      var self = this;
      this.panels = {};
      Array.prototype.forEach.call(this.querySelectorAll('[data-cf-state]'), function (el) {
        self.panels[el.getAttribute('data-cf-state')] = el;
      });
      this.live = this.querySelector('[data-cf-live]');
      this.wheel = this.querySelector('[data-cf-wheel]');
      this.wheelWrap = this.querySelector('[data-cf-wheel-wrap]');
      this.form = this.querySelector('[data-cf-form]');
      this.angle = 0;
      this.spinning = false;
    }

    bind() {
      var self = this;

      this.addEventListener('click', function (e) {
        var trigger = e.target && e.target.closest ? e.target.closest('[data-cf-action]') : null;
        if (!trigger || !self.contains(trigger)) return;
        if (trigger.getAttribute('data-cf-action') !== 'instagram') e.preventDefault();
        self.handleAction(trigger.getAttribute('data-cf-action'), trigger);
      });

      if (this.form) {
        // Echtes <form> mit <button type="submit">: Enter-Taste und native
        // Validierung bleiben erhalten. Die globale Schwarz-Faerbung durch
        // platform_customizations.custom_css wird im CSS ueberschrieben.
        this.form.addEventListener('submit', function (e) {
          e.preventDefault();
          self.submit();
        });
      }

      if (this.wheel) {
        this.wheel.addEventListener('transitionend', function (e) {
          if (e.propertyName === 'transform') self.onSpinEnd();
        });
      }
    }

    /* ------------------------------------------------- State-Verwaltung --- */

    ageConfirmed() {
      if (readJSON('local', AGE_KEY)) return true;
      var s = store('local');
      if (!s) return false;
      try {
        // Entscheidung des theme-eigenen Age-Gates respektieren, statt
        // Erstbesucher doppelt zu fragen (theme-report 3.6).
        return !!s.getItem(THEME_AGE_KEY);
      } catch (e) {
        return false;
      }
    }

    /**
     * Liest den Session-State und verwirft alles Inkonsistente — damit laesst
     * sich kein Schritt per Reload oder manipuliertem Storage ueberspringen.
     */
    restore() {
      var saved = readJSON('session', SESSION_KEY);
      var played = readJSON('local', PLAYED_KEY);
      var ageOk = this.ageConfirmed();

      this.prize = null;
      this.firstPrize = null;
      this.spins = 0;
      this.bonusUsed = false;
      this.bonusUnlocked = false;

      if (saved && FLOW.indexOf(saved.state) !== -1) {
        // Wurde das Theme-Gate zwischenzeitlich in einem anderen Tab bestaetigt,
        // nicht erneut fragen.
        if (saved.state === 'age' && ageOk) return 'intro';
        var prize = saved.prize || null;
        var spins = Number(saved.spins) || 0;
        var bonusUsed = !!saved.bonusUsed;
        var ok = true;

        if (saved.state !== 'age' && !ageOk) ok = false;
        if (spins > 0 && !prize) ok = false;
        if ((saved.state === 'form' || saved.state === 'done' || saved.state === 'bonus') && !prize) ok = false;
        if (saved.state === 'bonus' && (!this.o.bonusEnabled || bonusUsed || Number(prize && prize.tier) !== 1)) ok = false;
        if (spins > (bonusUsed ? 2 : 1)) ok = false;

        if (ok) {
          this.prize = prize;
          this.firstPrize = saved.firstPrize || null;
          this.spins = spins;
          this.bonusUsed = bonusUsed;
          this.bonusUnlocked = !!saved.bonusUnlocked;
          return saved.state;
        }
      }

      if (played && played.prize) {
        this.prize = played.prize;
        return 'already';
      }
      return ageOk ? 'intro' : 'age';
    }

    persist() {
      if (this.state === 'already' || this.state === 'denied') return;
      writeJSON('session', SESSION_KEY, {
        state: this.state,
        prize: this.prize,
        firstPrize: this.firstPrize,
        spins: this.spins,
        bonusUsed: this.bonusUsed,
        bonusUnlocked: this.bonusUnlocked,
        ts: Date.now()
      });
    }

    go(state, focus) {
      this.state = state;
      this.persist();
      this.render(focus !== false);
    }

    render(focus) {
      var self = this;
      Object.keys(this.panels).forEach(function (key) {
        var panel = self.panels[key];
        var active = key === self.state;
        panel.hidden = !active;
      });

      if (this.state === 'spin') {
        // Nach einem Reload den bereits gedrehten Stand wiederherstellen,
        // statt einen zweiten Dreh zu erlauben.
        if (this.prize && this.spins >= (this.bonusUsed ? 2 : 1)) this.showResult(this.spins > 1, true);
        else this.resetSpinPanel();
      }
      if (this.state === 'bonus') this.paintBonus();
      if (this.state === 'already' || this.state === 'done') this.paintFinalPrize(this.panels[this.state]);

      if (focus && !document.body.classList.contains('modal-opened')) {
        var target = this.panels[this.state] && this.panels[this.state].querySelector('[data-cf-focus]');
        if (target) {
          focusSoftly(target);
          var top = this.getBoundingClientRect().top + window.pageYOffset - 24;
          if (Math.abs(window.pageYOffset - top) > window.innerHeight * 0.4) {
            window.scrollTo({ top: top, behavior: reducedMotion() ? 'auto' : 'smooth' });
          }
        }
      }
    }

    announce(text) {
      if (this.live) this.live.textContent = String(text || '').replace(/\s+/g, ' ').trim();
    }

    /* ------------------------------------------------------- Aktionen --- */

    handleAction(action, trigger) {
      switch (action) {
        case 'age-yes':
          writeJSON('local', AGE_KEY, { ok: true, ts: Date.now() });
          this.go('intro');
          break;

        case 'age-no':
          this.state = 'denied';
          this.render(true);
          break;

        case 'start':
          this.go('spin');
          break;

        case 'spin':
          this.spin(trigger);
          break;

        case 'bonus':
          this.go('bonus');
          break;

        case 'instagram':
          // Honor-System: der Follow ist technisch nicht verifizierbar.
          this.bonusUnlocked = true;
          this.persist();
          this.paintBonus();
          this.announce(this.strings.bonusUnlocked || '');
          break;

        case 'bonus-spin':
          this.bonusUsed = true;
          this.go('spin');
          break;

        case 'to-form':
          this.go('form');
          break;

        case 'retry':
          this.submit();
          break;

        case 'replay':
          removeKey('local', PLAYED_KEY);
          removeKey('session', SESSION_KEY);
          this.prize = null;
          this.firstPrize = null;
          this.spins = 0;
          this.bonusUsed = false;
          this.bonusUnlocked = false;
          this.go(this.ageConfirmed() ? 'intro' : 'age');
          break;

        case 'reset': // nur im Theme-Editor sichtbar
          removeKey('local', PLAYED_KEY);
          removeKey('local', AGE_KEY);
          removeKey('session', SESSION_KEY);
          window.location.reload();
          break;

        default:
          break;
      }
    }

    /* ----------------------------------------------------------- Rad --- */

    resetSpinPanel() {
      var panel = this.panels.spin;
      if (!panel) return;
      var result = panel.querySelector('[data-cf-result]');
      var btn = panel.querySelector('[data-cf-action="spin"]');
      if (result) result.hidden = true;
      if (btn) {
        btn.hidden = false;
        btn.disabled = false;
        btn.textContent = this.strings.spinButton || 'Drehen';
      }
    }

    spin(button) {
      if (this.spinning) return;
      this.spinning = true;

      if (button) {
        button.disabled = true;
        button.textContent = this.strings.spinRunning || 'Dreht …';
      }
      if (this.wheelWrap) this.wheelWrap.setAttribute('aria-busy', 'true');
      this.announce(this.strings.spinRunning || 'Das Rad dreht.');

      this.pendingPrize = drawPrize(this.prizes);

      // Ein zum gezogenen Ergebnis passendes Segment auswaehlen.
      var candidates = [];
      for (var i = 0; i < SEGMENT_TIERS.length; i++) {
        if (SEGMENT_TIERS[i] === Number(this.pendingPrize.tier)) candidates.push(i);
      }
      if (!candidates.length) candidates = [0];
      var seg = candidates[Math.min(candidates.length - 1, Math.floor(randomFloat() * candidates.length))];

      var reduced = reducedMotion();
      var turns = reduced ? 1 : 5;
      var jitter = reduced ? 0 : (randomFloat() - 0.5) * (SEGMENT_DEG - 22);
      var targetMod = (((-(SEGMENT_DEG / 2) - seg * SEGMENT_DEG + jitter) % 360) + 360) % 360;
      var currentMod = ((this.angle % 360) + 360) % 360;
      var delta = (((targetMod - currentMod) % 360) + 360) % 360;
      this.angle += turns * 360 + delta;

      if (this.wheel) {
        this.wheel.classList.remove('cannafair-game__wheel--settle');
        this.wheel.classList.add('cannafair-game__wheel--spinning');
        this.wheel.style.transform = 'rotate(' + this.angle.toFixed(2) + 'deg)';
      }

      this.vibrateSpin(reduced);

      // Fallback, falls transitionend ausbleibt (Tab im Hintergrund, Motion off).
      var self = this;
      clearTimeout(this._spinTimer);
      this._spinTimer = setTimeout(function () {
        self.onSpinEnd();
      }, (reduced ? 900 : Number(this.o.spinDuration || 4.5) * 1000) + 700);
    }

    onSpinEnd() {
      if (!this.spinning) return;
      this.spinning = false;
      clearTimeout(this._spinTimer);

      if (this.wheel) {
        this.wheel.classList.remove('cannafair-game__wheel--spinning');
        if (!reducedMotion()) {
          // Kurzes Nachwippen — das Rad laeuft mechanisch aus.
          this.wheel.classList.add('cannafair-game__wheel--settle');
          this.angle -= 1.4;
          this.wheel.style.transform = 'rotate(' + this.angle.toFixed(2) + 'deg)';
        }
      }
      if (this.wheelWrap) this.wheelWrap.setAttribute('aria-busy', 'false');

      var isBonus = this.spins > 0;
      this.spins += 1;
      if (isBonus) {
        this.prize = betterPrize(this.firstPrize, this.pendingPrize);
      } else {
        this.prize = this.pendingPrize;
        this.firstPrize = this.pendingPrize;
      }
      this.persist();
      this.showResult(isBonus, false);
    }

    vibrateSpin(reduced) {
      if (reduced || !navigator.vibrate) return;
      try {
        navigator.vibrate([12, 60, 12, 80, 12, 110, 12, 150, 12, 210, 14, 300, 18, 430]);
      } catch (e) {}
    }

    vibrateWin(tier) {
      if (reducedMotion() || !navigator.vibrate) return;
      try {
        navigator.vibrate(Number(tier) === 3 ? [30, 60, 30, 60, 90] : [25, 70, 40]);
      } catch (e) {}
    }

    showResult(isBonus, silent) {
      var panel = this.panels.spin;
      if (!panel || !this.prize) return;
      var tier = Number(this.prize.tier);
      var headings = this.strings.resultHeadings || {};
      var texts = this.strings.resultTexts || {};

      var btn = panel.querySelector('[data-cf-action="spin"]');
      if (btn) btn.hidden = true;

      var head = panel.querySelector('[data-cf-result-heading]');
      var prizeEl = panel.querySelector('[data-cf-result-prize]');
      var textEl = panel.querySelector('[data-cf-result-text]');
      var bonusBtn = panel.querySelector('[data-cf-action="bonus"]');
      var nextBtn = panel.querySelector('[data-cf-action="to-form"]');
      var box = panel.querySelector('[data-cf-result]');

      if (head) head.textContent = headings[tier] || headings['1'] || '';
      if (prizeEl) prizeEl.textContent = this.prizeLabel(tier);
      if (textEl) textEl.innerHTML = texts[tier] || texts['1'] || '';
      if (box) box.hidden = false;

      var offerBonus = !!this.o.bonusEnabled && tier === 1 && !this.bonusUsed;
      if (bonusBtn) bonusBtn.hidden = !offerBonus;
      if (nextBtn) {
        nextBtn.hidden = false;
        nextBtn.textContent = offerBonus ? this.strings.skipBonus || 'Ohne Bonus weiter' : this.strings.toForm || 'Weiter';
        nextBtn.className =
          'cannafair-game__btn ' + (offerBonus ? 'cannafair-game__btn--ghost' : 'cannafair-game__btn--primary');
      }

      if (silent) return;

      this.announce(
        (isBonus ? (this.strings.bonusCounted || '') + ' ' : '') + (headings[tier] || '') + ' ' + this.prizeLabel(tier)
      );
      this.vibrateWin(tier);
      this.confetti(tier);
      if (head && !document.body.classList.contains('modal-opened')) focusSoftly(head);
    }

    prizeLabel(tier) {
      var t = Number(tier);
      for (var i = 0; i < this.prizes.length; i++) {
        if (Number(this.prizes[i].tier) === t) return this.prizes[i].prizeLabel || this.prizes[i].label || '';
      }
      return '';
    }

    confetti(tier) {
      if (reducedMotion()) return;
      var host = this.querySelector('[data-cf-confetti]');
      if (!host) return;
      var count = Number(tier) === 3 ? 22 : Number(tier) === 2 ? 14 : 9;
      var colors = ['var(--cf-accent)', '#ffffff', '#63af62'];
      var frag = document.createDocumentFragment();
      for (var i = 0; i < count; i++) {
        var piece = document.createElement('i');
        piece.style.left = (4 + randomFloat() * 92).toFixed(1) + '%';
        piece.style.background = colors[i % colors.length];
        piece.style.animationDelay = (randomFloat() * 260).toFixed(0) + 'ms';
        piece.style.setProperty('--cf-drift', ((randomFloat() - 0.5) * 90).toFixed(0) + 'px');
        frag.appendChild(piece);
      }
      host.textContent = '';
      host.appendChild(frag);
      clearTimeout(this._confettiTimer);
      this._confettiTimer = setTimeout(function () {
        host.textContent = '';
      }, 2400);
    }

    /* --------------------------------------------------------- Bonus --- */

    paintBonus() {
      var panel = this.panels.bonus;
      if (!panel) return;
      var go = panel.querySelector('[data-cf-action="bonus-spin"]');
      var hint = panel.querySelector('[data-cf-bonus-hint]');
      if (go) go.hidden = !this.bonusUnlocked;
      if (hint) hint.hidden = !this.bonusUnlocked;
    }

    /* ------------------------------------------------------ Formular --- */

    setFieldError(name, message) {
      var input = this.querySelector('[data-cf-input="' + name + '"]');
      var slot = this.querySelector('[data-cf-error="' + name + '"]');
      if (input) input.setAttribute('aria-invalid', message ? 'true' : 'false');
      if (slot) {
        slot.textContent = message || '';
        slot.hidden = !message;
      }
      return input;
    }

    validate() {
      var s = this.strings;
      var emailEl = this.querySelector('[data-cf-input="email"]');
      var consentEl = this.querySelector('[data-cf-input="consent"]');
      var ageEl = this.querySelector('[data-cf-input="age"]');
      var nameEl = this.querySelector('[data-cf-input="first_name"]');

      var email = emailEl ? String(emailEl.value || '').trim() : '';
      var emailBad = !EMAIL_RE.test(email);
      var consentBad = !!consentEl && !consentEl.checked;
      var ageBad = !!ageEl && !ageEl.checked;

      this.setFieldError('email', emailBad ? s.errEmail || 'Bitte gib eine gültige E-Mail-Adresse ein.' : '');
      this.setFieldError(
        'consent',
        consentBad ? s.errConsent || 'Ohne diese Zustimmung können wir dir den Code nicht schicken.' : ''
      );
      this.setFieldError('age', ageBad ? s.errAge || 'Bitte bestätige, dass du mindestens 18 Jahre alt bist.' : '');

      var firstBad = emailBad ? emailEl : consentBad ? consentEl : ageBad ? ageEl : null;
      if (firstBad) {
        this.announce(s.errForm || 'Bitte prüf die markierten Felder.');
        focusSoftly(firstBad);
        return null;
      }

      return { email: email, firstName: nameEl ? String(nameEl.value || '').trim() : '' };
    }

    utm() {
      var out = {};
      try {
        var params = new URLSearchParams(window.location.search);
        UTM_KEYS.forEach(function (k) {
          var v = params.get(k);
          if (v) out[k] = String(v).slice(0, 120);
        });
      } catch (e) {}
      return out;
    }

    notice(message, showRetry) {
      var slot = this.querySelector('[data-cf-notice]');
      var retry = this.querySelector('[data-cf-action="retry"]');
      if (slot) {
        slot.textContent = message || '';
        slot.hidden = !message;
      }
      if (retry) retry.hidden = !showRetry;
      if (message) this.announce(message);
    }

    setBusy(busy) {
      var btn = this.querySelector('[data-cf-submit]');
      if (!btn) return;
      btn.disabled = !!busy;
      btn.setAttribute('aria-busy', busy ? 'true' : 'false');
      btn.textContent = busy ? this.strings.submitBusy || 'Wird gesendet …' : this.strings.submit || 'Code sichern';
    }

    submit() {
      if (this._sending) return;

      var data = this._retryData || this.validate();
      if (!data) return;
      this._retryData = data;

      var s = this.strings;
      var self = this;

      if (!this.o.listId) {
        // Ohne List-ID wuerde Klaviyo hart ablehnen — der Lead ginge verloren.
        this.notice(
          s.errConfig || 'Die Anmeldung ist gerade nicht eingerichtet. Sprich uns bitte kurz am Stand B350 an.',
          false
        );
        if (window.console) console.warn('[cannafair-game] Section-Setting "klaviyo_list_id" ist leer.');
        return;
      }

      this._sending = true;
      this.setBusy(true);
      this.notice('', false);

      this.postSubscription(data, 'application/json')
        .then(function (res) {
          if (res.status >= 200 && res.status < 300) return true;
          if (res.status === 415) {
            // Fallback auf die JSON:API-Variante des Content-Type.
            return self.postSubscription(data, 'application/vnd.api+json').then(function (r2) {
              if (r2.status >= 200 && r2.status < 300) return true;
              throw { httpStatus: r2.status };
            });
          }
          throw { httpStatus: res.status };
        })
        .then(function () {
          self._sending = false;
          self._retryData = null;
          self.setBusy(false);
          writeJSON('local', PLAYED_KEY, { prize: self.prize, ts: Date.now() });
          self.go('done');
        })
        .catch(function (err) {
          self._sending = false;
          self.setBusy(false);
          var status = err && err.httpStatus;
          var msg;
          if (!status) {
            msg = s.errNetwork || 'Wir erreichen den Server gerade nicht. Prüf kurz deine Verbindung und versuch es erneut.';
          } else if (status === 429) {
            msg = s.errRate || 'Gerade sind viele gleichzeitig dran. Versuch es in ein paar Sekunden noch einmal.';
          } else if (status >= 500) {
            msg = s.errServer || 'Auf unserer Seite hakt es kurz. Versuch es gleich noch einmal.';
          } else {
            msg = s.errClient || 'Mit deinen Angaben stimmt etwas nicht. Prüf bitte deine E-Mail-Adresse.';
            self._retryData = null; // erneut validieren lassen
          }
          self.notice(msg, true);
          if (window.console) console.warn('[cannafair-game] subscribe failed', status || err);
        });
    }

    postSubscription(data, contentType) {
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = controller
        ? setTimeout(function () {
            controller.abort();
          }, 12000)
        : null;

      var props = {
        Messe: this.o.messe || 'Cannafair 2026',
        cannafair_prize: this.prizeLabel(this.prize && this.prize.tier),
        cannafair_prize_tier: Number(this.prize && this.prize.tier) || null,
        cannafair_source: this.o.source || 'flyer',
        cannafair_timestamp: new Date().toISOString(),
        cannafair_age_confirmed: true,
        cannafair_bonus_used: !!this.bonusUsed
      };

      var utm = this.utm();
      Object.keys(utm).forEach(function (k) {
        props[k] = utm[k];
      });

      // ⛔ Auflage 1: "Rabattstufen" darf NIEMALS gesetzt werden — die Live-Flows
      //    TspuaW und YeF6eh triggern auf Segmente, die an dieser Property haengen.
      delete props.Rabattstufen;

      var profile = {
        email: data.email,
        properties: props,
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
      };
      if (data.firstName) profile.first_name = data.firstName.slice(0, 60);

      var body = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: this.o.customSource || 'Cannafair 2026 Glücksrad',
            profile: { data: { type: 'profile', attributes: profile } }
          },
          relationships: { list: { data: { type: 'list', id: this.o.listId } } }
        }
      };

      return fetch(
        'https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(this.o.companyId || 'XqgAuU'),
        {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': contentType, revision: this.o.revision || '2026-07-15' },
          body: JSON.stringify(body),
          signal: controller ? controller.signal : undefined
        }
      )
        .then(function (res) {
          if (timer) clearTimeout(timer);
          return res;
        })
        .catch(function (e) {
          if (timer) clearTimeout(timer);
          throw e;
        });
    }

    /* -------------------------------------------------------- Ausgabe --- */

    paintFinalPrize(panel) {
      if (!panel || !this.prize) return;
      var slot = panel.querySelector('[data-cf-final-prize]');
      if (slot) slot.textContent = this.prizeLabel(this.prize.tier);
    }
  }

  customElements.define('cannafair-game', CannafairGame);

  // Theme-Editor: Section nach dem Neuladen erneut initialisieren.
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target && e.target.querySelector ? e.target.querySelector('cannafair-game') : null;
    if (el && !el._booted && typeof el.connectedCallback === 'function') el.connectedCallback();
  });
})();
